import { useEffect, useState } from "react";
import { PackagePlus, Plus, Box, Boxes, Check, Pencil, X } from "lucide-react";
import { computeCadPrice } from "../lib/cadPricing";
import { ZoomableImage } from "./ZoomableImage";

const CATEGORY_STYLES = {
  Bedding: "bg-blue-50 text-blue-700 border-blue-200",
  Bath: "bg-cyan-50 text-cyan-700 border-cyan-200",
  Kitchen: "bg-amber-50 text-amber-700 border-amber-200",
  Rug: "bg-orange-50 text-orange-700 border-orange-200",
  Window: "bg-violet-50 text-violet-700 border-violet-200",
  Pet: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// iter 178: Card selection checkbox — reused by both Compact and Detailed
// variants. Absolute-positioned top-left with a semi-opaque background so it
// stays legible over any photo. Clicking it toggles selection without opening
// the detail dialog (stopPropagation).
function CardSelectCheckbox({ productId, checked, onToggle, size = 18 }) {
  if (!onToggle) return null;
  return (
    <button
      type="button"
      data-testid={`card-select-${productId}`}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      title={checked ? "Deselect" : "Select"}
      className={`absolute top-1.5 left-1.5 rounded-md border transition-all flex items-center justify-center shadow-sm z-10 ${
        checked
          ? "bg-brand-600 border-brand-600 text-white opacity-100"
          : "bg-white/85 backdrop-blur border-gray-300 text-transparent hover:text-gray-400 opacity-70 hover:opacity-100"
      }`}
      style={{ width: size, height: size }}
    >
      <Check size={size - 6} strokeWidth={3} />
    </button>
  );
}

function fmtPrice(value, currency) {
  if (value == null) return "-";
  const symbol = currency === "CAD" ? "C$" : "$";
  return `${symbol}${Number(value).toFixed(2)}`;
}

function salesPriceOf(entry, currency) {
  return currency === "CAD" ? entry.suggested_price_cad : entry.suggested_price_usd;
}

function priceRange(entries, currency) {
  const values = entries.map((e) => salesPriceOf(e, currency)).filter((v) => v != null);
  if (values.length === 0) return "-";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const symbol = currency === "CAD" ? "C$" : "$";
  if (min === max) return `${symbol}${min.toFixed(2)}`;
  return `${symbol}${min.toFixed(2)} – ${symbol}${max.toFixed(2)}`;
}

function EmptyState({ testId, message }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl py-24 text-center" data-testid={testId}>
      <p className="text-sm text-gray-400">{message}</p>
    </div>
  );
}

function CompactCard({ product, onAddVariant, onCartClick, onCardClick, priceCurrency, selected, onToggleSelect, readOnly }) {
  const first = product.entries[0];
  const title = `${first.item_no}${product.entries.length > 1 ? "*" : ""}`;
  const totalQty = product.entries.reduce((s, e) => s + (e.qty || 0), 0);
  const contractNo = product.contract_no || first.contract_no;
  return (
    <div
      key={product.product_id}
      className={`group h-full bg-white border rounded-lg flex flex-col cursor-pointer transition-all ${
        selected ? "border-brand-500 ring-1 ring-brand-500 shadow-sm" : "border-gray-200 hover:border-brand-200 hover:shadow-sm"
      }`}
      data-testid={`product-card-${product.product_id}`}
      onClick={() => onCardClick(product)}
    >
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        <ZoomableImage
          src={product.photo_url}
          alt={product.description}
          className="w-full h-full object-cover rounded-t-lg"
          previewSize={280}
          testId={`card-photo-${product.product_id}`}
        />
        <CardSelectCheckbox productId={product.product_id} checked={selected} onToggle={onToggleSelect} size={18} />
        {!readOnly && (
          <button
            data-testid={`cart-button-${product.product_id}`}
            onClick={(e) => { e.stopPropagation(); onCartClick(product); }}
            title="Add to Quotation"
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md bg-white/90 backdrop-blur border border-gray-200 flex items-center justify-center text-gray-600 hover:text-brand-600 hover:border-brand-200 transition-colors shadow-sm opacity-0 group-hover:opacity-100"
          >
            <PackagePlus size={12} strokeWidth={1.75} />
          </button>
        )}
        {product.entries.length > 1 && (
          <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 text-white text-[9px] font-mono">
            {product.entries.length} sizes
          </span>
        )}
      </div>

      <div className="p-2 flex flex-col gap-0.5">
        <h3 className="font-mono text-[11px] font-bold text-gray-900 truncate" data-testid={`card-itemno-${product.product_id}`}>{title}</h3>
        <p className="text-[10px] text-gray-500 truncate leading-tight" title={product.description}>{product.description || "-"}</p>
        {contractNo && (
          <p className="font-mono text-[9px] text-gray-400 truncate" data-testid={`card-contract-${product.product_id}`} title={`Contract ${contractNo}`}>
            Contract {contractNo}
          </p>
        )}
        <div className="flex items-center justify-between gap-1 pt-1 mt-1 border-t border-gray-100">
          <span className="font-mono text-[10px] text-gray-400" data-testid={`card-qty-${product.product_id}`}>{totalQty.toLocaleString()} pcs</span>
          <span className="font-mono text-[11px] font-bold text-gray-900 truncate" data-testid={`card-price-${product.product_id}`}>{priceRange(product.entries, priceCurrency)}</span>
        </div>
      </div>
    </div>
  );
}


const EDIT_INPUT = "w-full min-w-0 h-7 font-mono text-right text-[11px] rounded-md border border-brand-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-1";

// iter 258: one editable size row. Size / RMB / QTY_H_Q / USD / CAD inputs;
// CAD previews live from the RMB + QTY_H_Q + duty being typed (mirrors the
// backend formula) and is saved as a manual override only when typed directly.
function EditableEntryRow({ entry, product, rates, dutyDraft, onEditEntry }) {
  const [rmb, setRmb] = useState(entry.purchase_price ?? "");
  const [qtyHq, setQtyHq] = useState(entry.qty_40ft ?? "");
  const [cad, setCad] = useState(entry.suggested_price_cad ?? "");
  const [cadTouched, setCadTouched] = useState(false);
  useEffect(() => { setRmb(entry.purchase_price ?? ""); }, [entry.purchase_price]);
  useEffect(() => { setQtyHq(entry.qty_40ft ?? ""); }, [entry.qty_40ft]);
  useEffect(() => { setCad(entry.suggested_price_cad ?? ""); setCadTouched(false); }, [entry.suggested_price_cad]);

  // Live preview: whenever an input of the formula changes, show the computed CAD.
  useEffect(() => {
    if (cadTouched || !rates) return;
    const v = computeCadPrice({
      purchasePrice: rmb === "" ? null : rmb,
      entry, product, rates,
      qty40ftOverride: qtyHq === "" ? null : qtyHq,
      dutyRate: dutyDraft === "" || dutyDraft == null ? undefined : dutyDraft,
    });
    if (v != null) setCad(v);
  }, [rmb, qtyHq, dutyDraft, rates, entry, product, cadTouched]);

  const num = (raw) => { const t = String(raw).trim(); return t === "" ? null : Number(t); };
  const save = (patch) => onEditEntry && onEditEntry(entry.entry_id, patch);

  return (
    <div className="py-2 space-y-1.5" data-testid={`card-variant-row-${entry.entry_id}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <input
          data-testid={`edit-size-${entry.entry_id}`}
          type="text"
          defaultValue={entry.size ?? ""}
          placeholder="Size"
          onBlur={(e) => { const v = e.target.value.trim(); if (v !== (entry.size || "")) save({ size: v }); }}
          className="flex-1 min-w-0 h-7 text-[11px] font-medium text-gray-700 rounded-md border border-brand-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-1.5"
        />
        <span className="font-mono text-gray-400 text-[10px] shrink-0">Qty {entry.qty ?? "-"}</span>
      </div>
      <div className="grid grid-cols-4 gap-1">
        <label className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[9px] uppercase tracking-label text-gray-400">RMB</span>
          <input data-testid={`edit-purchase-price-${entry.entry_id}`} type="number" step="0.01" value={rmb} placeholder="¥"
            onChange={(e) => setRmb(e.target.value)}
            onBlur={(e) => { const v = num(e.target.value); if ((v ?? null) !== (entry.purchase_price ?? null)) save({ purchase_price: v, sale_price_cad: null }); }}
            className={EDIT_INPUT} />
        </label>
        <label className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[9px] uppercase tracking-label text-gray-400">QTY_H_Q</span>
          <input data-testid={`edit-qty-40ft-${entry.entry_id}`} type="number" step="1" value={qtyHq} placeholder="auto"
            onChange={(e) => setQtyHq(e.target.value)}
            onBlur={(e) => { const t = e.target.value.trim(); const v = t === "" ? null : parseInt(t, 10); if ((v ?? null) !== (entry.qty_40ft ?? null)) save({ qty_40ft: v, sale_price_cad: null }); }}
            className={EDIT_INPUT} />
        </label>
        <label className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[9px] uppercase tracking-label text-gray-400">USD</span>
          <input key={`usd-${entry.entry_id}-${entry.suggested_price_usd ?? "null"}`} data-testid={`edit-price-usd-${entry.entry_id}`} type="number" step="0.01" defaultValue={entry.suggested_price_usd ?? ""} placeholder="$"
            onBlur={(e) => { const v = num(e.target.value); if ((v ?? null) !== (entry.suggested_price_usd ?? null)) save({ sale_price_usd: v }); }}
            className={EDIT_INPUT} />
        </label>
        <label className="flex flex-col gap-0.5 min-w-0">
          <span className="text-[9px] uppercase tracking-label text-gray-400 flex items-center justify-between">CAD{entry.sale_price_cad_manual && <span className="text-[8px] normal-case text-amber-600" title="Manual override">manual</span>}</span>
          <input data-testid={`edit-price-cad-${entry.entry_id}`} type="number" step="0.01" value={cad} placeholder="C$"
            onChange={(e) => { setCadTouched(true); setCad(e.target.value); }}
            onBlur={(e) => { const v = num(e.target.value); if (cadTouched && (v ?? null) !== (entry.suggested_price_cad ?? null)) save({ sale_price_cad: v }); }}
            className={`${EDIT_INPUT} ${entry.sale_price_cad_manual ? "border-amber-300" : ""}`} />
        </label>
      </div>
    </div>
  );
}

function DetailedCard({ product, onAddVariant, onCartClick, onCardClick, priceCurrency, selected, onToggleSelect, readOnly, editable, canEdit, editingId, onToggleEdit, onEditProduct, onEditEntry, onRemove, rates }) {
  // iter 258: duty draft lives on the card so every size row can preview
  // CAD with the value being typed (before blur-save).
  const [dutyDraft, setDutyDraft] = useState(product.customs_duty_rate_is_set ? product.customs_duty_rate : "");
  useEffect(() => { setDutyDraft(product.customs_duty_rate_is_set ? product.customs_duty_rate : ""); }, [product.customs_duty_rate, product.customs_duty_rate_is_set]);
  const first = product.entries[0];
  const title = `${first.item_no}${product.entries.length > 1 ? "*" : ""}`;
  const totalQty = product.entries.reduce((s, e) => s + (e.qty || 0), 0);
  const catStyle = CATEGORY_STYLES[product.category] || "bg-gray-100 text-gray-600 border-gray-200";
  // iter 178q: per-product Edit toggle sits at the card's top-right when
  // canEdit=true (admin viewing a share link). editable is the derived
  // "am I the currently-open editing card" flag.
  const isEditingThisCard = editable;
  return (
    <div
      key={product.product_id}
      className={`h-full bg-white border rounded-xl flex flex-col cursor-pointer transition-all overflow-hidden ${
        selected ? "border-brand-500 ring-1 ring-brand-500 shadow-md" : "border-gray-200 hover:border-brand-200 hover:shadow-md"
      }`}
      data-testid={`product-card-${product.product_id}`}
      onClick={() => onCardClick(product)}
    >
      <div className="relative aspect-[4/3] bg-gray-50 overflow-hidden flex-shrink-0">
        <ZoomableImage
          src={product.photo_url}
          alt={product.description}
          className="w-full h-full object-cover"
          previewSize={340}
          testId={`card-photo-${product.product_id}`}
        />
        <CardSelectCheckbox productId={product.product_id} checked={selected} onToggle={onToggleSelect} size={22} />
        {!readOnly && (
          <button
            data-testid={`cart-button-${product.product_id}`}
            onClick={(e) => { e.stopPropagation(); onCartClick(product); }}
            title="Add to Quotation"
            className="absolute top-3 right-3 h-8 px-3 rounded-lg bg-white border border-gray-200 flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-brand-700 hover:border-brand-300 transition-colors shadow-sm"
          >
            <PackagePlus size={13} strokeWidth={1.75} /> Quote
          </button>
        )}
        {/* iter 178q: per-product Edit toggle only shown to admins viewing
            a share link. Sits top-right of the photo; clicking flips the
            card into inline-editing mode without opening the detail dialog. */}
        {canEdit && (
          <button
            data-testid={`card-edit-toggle-${product.product_id}`}
            onClick={(e) => { e.stopPropagation(); onToggleEdit && onToggleEdit(product.product_id); }}
            title={isEditingThisCard ? "Done editing" : "Edit product"}
            className={`absolute top-3 right-3 h-8 w-8 rounded-lg flex items-center justify-center shadow-sm transition-colors ${isEditingThisCard ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-white border border-gray-200 text-gray-600 hover:text-brand-700 hover:border-brand-300"}`}
          >
            {isEditingThisCard ? <Check size={14} strokeWidth={2} /> : <Pencil size={13} strokeWidth={1.75} />}
          </button>
        )}
        {/* iter 178s: X button removes the product from the share link. Only
            shown to admins/BH admins on shared pages (canEdit). Sits left of
            the Pencil so both stay top-right and never overlap the photo. */}
        {canEdit && onRemove && (
          <button
            data-testid={`card-remove-${product.product_id}`}
            onClick={(e) => { e.stopPropagation(); onRemove(product.product_id); }}
            title="Remove from list"
            className="absolute top-3 right-[3.25rem] h-8 w-8 rounded-lg flex items-center justify-center shadow-sm bg-white border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-300 transition-colors"
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}
        {product.is_bulky && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/95 backdrop-blur text-[10px] font-semibold text-orange-700 border border-orange-200">
            <Box size={10} strokeWidth={2} /> Bulky
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1 gap-3">
        <div>
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-mono text-sm font-bold text-gray-900 truncate" data-testid={`card-itemno-${product.product_id}`}>{title}</h3>
            {!readOnly && (
              <button
                data-testid={`add-variant-${product.product_id}`}
                onClick={(e) => { e.stopPropagation(); onAddVariant({ ...first, product_id: product.product_id, description: product.description }); }}
                title="Add Variant"
                className="flex-shrink-0 h-6 w-6 rounded-md border border-gray-200 flex items-center justify-center text-gray-400 hover:text-brand-600 hover:border-brand-300 transition-colors"
              >
                <Plus size={12} strokeWidth={2} />
              </button>
            )}
          </div>
          {editable ? (
            <textarea
              data-testid={`edit-description-${product.product_id}`}
              defaultValue={product.description || ""}
              placeholder="Description (editable, auto-saved)"
              onClick={(e) => e.stopPropagation()}
              onBlur={(e) => { if (e.target.value !== (product.description || "")) onEditProduct && onEditProduct(product.product_id, { description: e.target.value }); }}
              rows={2}
              className="mt-1 w-full text-xs text-gray-700 leading-snug rounded-md border border-brand-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-2 py-1.5 resize-y"
            />
          ) : (
            <p className="text-xs text-gray-600 mt-1 line-clamp-2 leading-snug" data-testid={`card-description-${product.product_id}`} title={product.description}>{product.description || "-"}</p>
          )}
          {!editable && product.specification && (
            <p className="text-[11px] text-gray-500 mt-1 whitespace-pre-line" data-testid={`card-specification-${product.product_id}`} title={product.specification}>{product.specification}</p>
          )}
          {editable && (
            <textarea
              data-testid={`edit-specification-${product.product_id}`}
              defaultValue={product.specification || ""}
              placeholder="Specification (editable, auto-saved)"
              onClick={(e) => e.stopPropagation()}
              onBlur={(e) => { if (e.target.value !== (product.specification || "")) onEditProduct && onEditProduct(product.product_id, { specification_product: e.target.value }); }}
              rows={2}
              className="mt-1.5 w-full text-[11px] text-gray-600 leading-snug rounded-md border border-brand-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-2 py-1.5 resize-y"
            />
          )}
          {(product.contract_no || first.contract_no) && (
            <p className="font-mono text-[11px] text-gray-500 mt-1.5" data-testid={`card-contract-${product.product_id}`}>
              Contract {product.contract_no || first.contract_no}
              {product.customer_name && (
                <span className="font-sans text-gray-400 ml-2" title={product.customer_name}>· {product.customer_name}</span>
              )}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {product.category && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border ${catStyle}`}>{product.category}</span>
            )}
            {product.subcategory && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-50 text-gray-600 border border-gray-200">{product.subcategory}</span>
            )}
            {product.product_type && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-50 text-gray-600 border border-gray-200">{product.product_type}</span>
            )}
          </div>
        </div>

        {!readOnly && (
        <div className="grid grid-cols-3 gap-2 py-2 border-y border-gray-100">
          <div>
            <p className="text-[9px] uppercase tracking-label text-gray-400">Total Qty</p>
            <p className="font-mono text-sm font-semibold text-gray-900 mt-0.5" data-testid={`card-total-qty-${product.product_id}`}>{totalQty.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-label text-gray-400">CBM</p>
            <p className="font-mono text-sm font-semibold text-gray-900 mt-0.5" data-testid={`card-cbm-${product.product_id}`}>{product.cbm_sum.toFixed(3)}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-label text-gray-400 flex items-center gap-0.5"><Boxes size={9} strokeWidth={2} /> Sizes</p>
            <p className="font-mono text-sm font-semibold text-gray-900 mt-0.5">{product.entries.length}</p>
          </div>
        </div>
        )}

        <div className="flex-1 min-h-0">
          {editable ? (
            <>
              {/* iter 178q: expanded price columns while editing — Size, RMB
                  cost, QTY_H_Q override, USD, CAD. Renders as a vertical
                  block per entry to keep field labels legible; the read-only
                  view keeps its compact 4-column grid. */}
              <div className="text-[9px] uppercase tracking-label text-gray-400 pb-1.5 border-b border-gray-100">Sizes ({product.entries.length})</div>
              {/* iter 258: product-level duty input — feeds the CAD formula
                  for every size row below. */}
              <div className="flex items-center justify-between py-1.5 border-b border-gray-100" onClick={(e) => e.stopPropagation()}>
                <span className="text-[9px] uppercase tracking-label text-gray-400">Duty %</span>
                <input
                  data-testid={`edit-duty-${product.product_id}`}
                  type="number" step="0.1"
                  value={dutyDraft ?? ""}
                  placeholder="18"
                  onChange={(e) => setDutyDraft(e.target.value)}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    const v = raw === "" ? null : Number(raw);
                    if ((v ?? null) !== (product.customs_duty_rate_is_set ? product.customs_duty_rate : null)) {
                      onEditProduct && onEditProduct(product.product_id, { customs_duty_rate: v });
                      // duty changes CAD for every size → drop manual CAD overrides
                      product.entries.forEach((en) => { if (en.sale_price_cad_manual) onEditEntry && onEditEntry(en.entry_id, { sale_price_cad: null }); });
                    }
                  }}
                  className="w-20 h-7 font-mono text-right text-[11px] rounded-md border border-brand-200 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-1"
                />
              </div>
              <div className="max-h-[24rem] overflow-y-auto divide-y divide-gray-100">
                {product.entries.map((entry) => (
                  <EditableEntryRow
                    key={entry.entry_id}
                    entry={entry}
                    product={product}
                    rates={rates}
                    dutyDraft={dutyDraft}
                    onEditEntry={onEditEntry}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 text-[9px] uppercase tracking-label text-gray-400 pb-1.5 border-b border-gray-100">
            <span>Size</span>
            <span className="text-right">Qty</span>
            <span className="text-right">USD</span>
            <span className="text-right">CAD</span>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {product.entries.map((entry) => {
              const isHighlight = (priceCurrency === "USD" ? "USD" : "CAD");
              return (
                <div
                  key={entry.entry_id}
                  className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 items-center py-1.5 border-b border-gray-50 text-[11px]"
                  data-testid={`card-variant-row-${entry.entry_id}`}
                >
                  <span className="text-gray-700 truncate font-medium" title={entry.size || entry.item_no}>{entry.size || entry.item_no}</span>
                  <span className="font-mono text-right text-gray-500">{entry.qty ?? "-"}</span>
                  <span className={`font-mono text-right ${isHighlight === "USD" ? "font-bold text-gray-900" : "text-gray-500"}`}>{fmtPrice(entry.suggested_price_usd, "USD")}</span>
                  <span className={`font-mono text-right ${isHighlight === "CAD" ? "font-bold text-gray-900" : "text-gray-500"}`}>{fmtPrice(entry.suggested_price_cad, "CAD")}</span>
                </div>
              );
            })}
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GridView({
  products,
  onAddVariant,
  onCartClick,
  onCardClick,
  priceCurrency = "USD",
  loading,
  density = "compact",
  selected,
  onToggleSelected,
  readOnly = false,
  // iter 178q: canEdit controls whether the top-right Pencil button appears
  // (admin viewing a share link); editingId is the product_id currently in
  // edit mode; toggling one card auto-closes any other open one.
  canEdit = false,
  editingId = null,
  onToggleEdit,
  onEditProduct,
  onEditEntry,
  onRemove,
  rates = null,
}) {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl py-24 text-center text-sm text-gray-400" data-testid="products-loading">
        Loading...
      </div>
    );
  }
  if (products.length === 0) {
    return <EmptyState testId="products-empty-state" message="No products found matching your criteria" />;
  }

  const isSelected = (pid) => (selected instanceof Set ? selected.has(pid) : false);
  const toggle = (product) => onToggleSelected && onToggleSelected(product.product_id, product);

  if (density === "detailed") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="products-grid-container">
        {products.map((p) => (
          <DetailedCard
            key={p.product_id}
            product={p}
            onAddVariant={onAddVariant}
            onCartClick={onCartClick}
            onCardClick={onCardClick}
            priceCurrency={priceCurrency}
            selected={isSelected(p.product_id)}
            onToggleSelect={() => toggle(p)}
            readOnly={readOnly}
            canEdit={canEdit}
            editable={canEdit && editingId === p.product_id}
            onToggleEdit={onToggleEdit}
            onEditProduct={onEditProduct}
            onEditEntry={onEditEntry}
            onRemove={onRemove}
            rates={rates}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2" data-testid="products-grid-container">
      {products.map((p) => (
        <CompactCard
          key={p.product_id}
          product={p}
          onAddVariant={onAddVariant}
          onCartClick={onCartClick}
          onCardClick={onCardClick}
          priceCurrency={priceCurrency}
          selected={isSelected(p.product_id)}
          onToggleSelect={() => toggle(p)}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}
